import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../api/client";
import { Modal } from "./Modal";
import { Avatar } from "./Avatar";
import { formatRelativeTime } from "../util/date";
import { describe } from "../util/activity";

interface BoardActivityProps {
  boardId: string;
  onClose: () => void;
}

/** Board-wide activity feed: recent events across every card, newest first. */
export function BoardActivity({ boardId, onClose }: BoardActivityProps) {
  const activityQ = useQuery({
    queryKey: ["board-activity", boardId],
    queryFn: () => api.boards.activity(boardId),
    // match the board's polling cadence so the feed stays fresh
    refetchInterval: 10_000,
  });

  const rows = activityQ.data ?? [];

  return (
    <Modal onClose={onClose} ariaLabel="Board activity">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Board activity</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {activityQ.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No activity yet.</p>
        ) : (
          <ul className="space-y-3">
            {rows.map((a) => {
              const name =
                a.actorDisplayName ?? a.actorUsername ?? "Someone";
              return (
                <li key={a.id} className="flex items-start gap-2 text-sm">
                  <Avatar userId={a.userId ?? "system"} name={name} />
                  <div className="pt-0.5 text-slate-600">
                    <span className="font-medium text-slate-800">{name}</span>{" "}
                    {describe(a)} on{" "}
                    <Link
                      to={`/boards/${boardId}/cards/${a.cardId}`}
                      onClick={onClose}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {a.cardTitle}
                    </Link>
                    <span className="ml-1.5 text-xs text-slate-400">
                      {formatRelativeTime(a.createdAt)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
